// src/components/MainLayout.tsx
import React, { useState, useEffect } from 'react';
import { AdminAccessProvider, useAdminAccess } from '../../contexts/AdminAccessContext';
import { Layout, Menu, Skeleton, Button } from 'antd';
import { UserOutlined, HomeOutlined, FileOutlined, TeamOutlined, LogoutOutlined, ArrowsAltOutlined, ClockCircleOutlined, PaperClipOutlined, LockOutlined, UnlockOutlined, IdcardOutlined } from '@ant-design/icons';
import { isProduction } from '../../utils/environment';
import type { MenuProps } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import './MainLayout.css';
import logo from '../../public/images/logo.svg';
import { decodeToken } from 'react-jwt';
import { gql, useQuery } from '@apollo/client';

const { Sider, Content } = Layout;

export const GET_USER_FIRST_LAST_NAME = gql`
  query GetUserFirstLastName($userId: Float!) {
    user(id: $userId) {
      firstName
      lastName
    }
  }
`;

interface GetUserFirstLastNameData {
  user: {
    firstName: string;
    lastName: string;
  };
}

interface GetUserFirstLastNameVars {
  userId: number;
}

// Component to handle the admin toggle and menu items
const MainLayoutContent: React.FC = () => {
  const { hasAdminAccess, isTrueAdmin, toggleAdminAccess } = useAdminAccess();

  const { loading, data } = useQuery<GetUserFirstLastNameData, GetUserFirstLastNameVars>(
    GET_USER_FIRST_LAST_NAME,
    {
      variables: { userId: Number(localStorage.getItem('userId')) },
    }
  );

  const userName = `${data?.user?.firstName} ${data?.user?.lastName}`; // Replace with actual user name
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const token = localStorage.getItem('accessToken');
  const result: any = decodeToken(token as string);
  const role = result?.role;
  const loggedInUserId = result?.id;
  const storedUserId = Number(localStorage.getItem('userId'));
  const isImpersonating =
    hasAdminAccess && loggedInUserId && storedUserId !== loggedInUserId;

  // Check if user has admin privileges based on role
  const isSuperAdmin = role === 'super_admin';
  const isAdminOrSuperAdmin = ['super_admin', 'admin'].includes(role);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'auth/login') {
      localStorage.clear();
    }
    navigate(`/${e.key}`);
  };

  // Admin specific client menu items (currently none)
  const adminClientMenuItems: MenuProps['items'] = [];

  // Client menu items - visible to all users
  const clientMenuItems: MenuProps['items'] = [
    {
      key: 'accounts',
      icon: <UserOutlined />,
      label: 'Accounts'
    },
    {
      key: 'documents',
      icon: <FileOutlined />,
      label: 'Documents'
    },
    {
      key: 'reports',
      icon: <FileOutlined />,
      label: 'Reports'
    },
    {
      key: 'profile',
      icon: <IdcardOutlined />,
      label: 'Profile'
    }
  ];

  // Admin menu items - only visible to admin roles
  const adminMenuItems: MenuProps['items'] = [
    {
      key: 'statements',
      icon: <PaperClipOutlined />,
      label: 'Statements',
      style: { display: hasAdminAccess ? 'block' : 'none' }
    },
    {
      key: 'scheduled-payments',
      icon: <ClockCircleOutlined />,
      label: 'Scheduled Payments',
      style: { display: hasAdminAccess ? 'block' : 'none' }
    },
    {
      key: 'transfers',
      icon: <ArrowsAltOutlined />,
      label: 'Transfers',
      style: { display: isSuperAdmin ? 'block' : 'none' }
    },
    {
      key: 'documents-upload',
      icon: <FileOutlined />,
      label: 'Documents Upload',
      style: { display: hasAdminAccess ? 'block' : 'none' }
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: 'Users',
      style: { display: isAdminOrSuperAdmin ? 'block' : 'none' }
    },
    {
      key: 'admin-settings',
      icon: <TeamOutlined />,
      label: 'Admin Settings',
      style: { display: isAdminOrSuperAdmin ? 'block' : 'none' }
    },
  ];

  // Logout item - always visible at the bottom
  const logoutMenuItem: MenuProps['items'] = [
    {
      key: 'auth/login',
      icon: <LogoutOutlined />,
      label: 'Logout',
      style: {
        position: 'absolute',
        bottom: 50,
        zIndex: 1,
        transition: 'all 0.2s',
      }
    }
  ];

  // Set the default selected key based on the current path
  const [selectedKeys, setSelectedKeys] = useState<string[]>([location.pathname.slice(1)]);

  // Update the selected key when the location changes
  useEffect(() => {
    setSelectedKeys([location.pathname.slice(1)]);
  }, [location.pathname]);

  return (
    <Layout style={{ minHeight: '100vh', maxHeight: '100vh', overflow: "hidden" }}>
      <Sider theme="light" width={220} collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
        <div className="logo">
          <img src={logo} alt="Logo" className="logo-img" />
        </div>
        {collapsed ? null : (
          <div className="user-name">
            {loading ? (
              <><Skeleton.Button active={false} size="small" /> <Skeleton.Button active={false} size="small" /></>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{userName}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Temporary Admin Access Toggle Button - Only visible to true admins in non-production environments */}
        {!collapsed && !isProduction() && isTrueAdmin && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Button 
              type="primary" 
              onClick={toggleAdminAccess}
              icon={hasAdminAccess ? <UnlockOutlined /> : <LockOutlined />}
              size="small"
              style={{ marginBottom: '10px' }}
            >
              {hasAdminAccess ? 'Disable Admin' : 'Enable Admin'}
            </Button>
          </div>
        )}
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={handleMenuClick}
          style={{ height: 'calc(100% - 48px)', borderRight: 0 }}
          items={[
            ...(!(
                isAdminOrSuperAdmin &&
                hasAdminAccess &&
                !isImpersonating
              )
              ? [{
                  key: 'client-group',
                  type: 'group',
                  label: 'Client',
                  children: clientMenuItems
                }]
              : []),
            ...(hasAdminAccess
              ? [
                  {
                    key: 'admin-group',
                    type: 'group' as any,
                    label: 'Admin',
                    children: adminMenuItems
                  }
                ]
              : []),
            ...logoutMenuItem
          ]}
        />
      </Sider>
      <Layout>
        <Content
          style={{
            padding: '0 24px 0 24px',
            minHeight: '100%',
            overflow: 'auto'
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

// Wrapper component that provides the context
const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('accessToken');
  const result: any = decodeToken(token as string);
  const role = result?.role;

  useEffect(() => {
    if (!token) return;
    const decoded: any = decodeToken(token);
    const exp = decoded?.exp;
    if (!exp) return;
    const timeout = exp * 1000 - Date.now();
    if (timeout <= 0) {
      localStorage.clear();
      navigate('/auth/login');
      return;
    }
    const id = setTimeout(() => {
      localStorage.clear();
      navigate('/auth/login');
    }, timeout);
    return () => clearTimeout(id);
  }, [token, navigate]);

  // Check if user has admin privileges based on role
  const initialAdminAccess = ['super_admin', 'admin', 'billpayer'].includes(role);
  const isTrueAdmin = ['super_admin', 'admin'].includes(role);

  return (
    <AdminAccessProvider initialAdminAccess={initialAdminAccess} isTrueAdmin={isTrueAdmin}>
      <MainLayoutContent />
    </AdminAccessProvider>
  );
};

export default MainLayout;
